"use client"

/**
 * Enhanced Calendar Component
 * 
 * This implementation is inspired by and partially based on the enhanced date picker
 * from https://date-picker.luca-felix.com/ by Luca Ziegler Félix.
 * 
 * Key enhancements over the standard shadcn calendar:
 * - Multi-mode navigation (Days, Months, Years)
 * - Improved navigation patterns for better UX
 * - Better grid layouts and spacing
 * 
 * Original concept and some layout patterns adapted from:
 * https://date-picker.luca-felix.com/
 */

import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { 
  DayPicker, 
  useDayPicker,
  labelNext,
  labelPrevious,
  type DayPickerProps 
} from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button, buttonVariants } from "@/components/ui/button"

type NavigationMode = "days" | "months" | "years"

export type CalendarProps = DayPickerProps & {
  /**
   * In the year view, the number of years to display at once.
   * @default 12
   */
  yearRange?: number
  
  /**
   * Whether to show the navigation mode switcher in the caption.
   * @default true
   */
  showNavigationSwitcher?: boolean
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  yearRange = 12,
  showNavigationSwitcher = true,
  numberOfMonths,
  components,
  ...props
}: CalendarProps) {
  const [navMode, setNavMode] = React.useState<NavigationMode>("days")
  const [currentYear, setCurrentYear] = React.useState(new Date().getFullYear())
  const [yearRangeStart, setYearRangeStart] = React.useState(() => {
    const currentYear = new Date().getFullYear()
    return currentYear - Math.floor(yearRange / 2)
  })

  const columnsDisplayed = navMode === "days" ? numberOfMonths : 1

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      style={{
        width: 293.6 * (columnsDisplayed ?? 1) + "px",
      }}
      classNames={{
        months: "relative flex",
        month: "w-full",
        month_caption: "relative mx-10 flex h-7 items-center justify-center",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-1 z-10"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-1 z-10"
        ),
        month_grid: "mx-auto mt-4",
        weekdays: "flex",
        weekday: "text-muted-foreground w-9 h-9 font-normal text-[0.8rem] text-center flex items-center justify-center",
        week: "flex w-max",
        day: "h-9 w-9 text-center text-sm p-0 relative flex items-center justify-center flex-1",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
        ),
        selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        today: "bg-accent text-accent-foreground",
        outside:
          "text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight
          return <Icon className="h-4 w-4" />
        },
        Nav: (navProps) => (
          <NavigationComponent
            {...navProps}
            navMode={navMode}
            currentYear={currentYear}
            setCurrentYear={setCurrentYear}
            yearRangeStart={yearRangeStart}
            setYearRangeStart={setYearRangeStart}
            yearRange={yearRange}
          />
        ),
        CaptionLabel: (captionProps) => (
          <CaptionLabelComponent
            {...captionProps}
            navMode={navMode}
            setNavMode={setNavMode}
            currentYear={currentYear}
            yearRangeStart={yearRangeStart}
            yearRange={yearRange}
            showNavigationSwitcher={showNavigationSwitcher}
          />
        ),
        MonthGrid: (gridProps) => (
          <MonthGridComponent
            {...gridProps}
            navMode={navMode}
            setNavMode={setNavMode}
            currentYear={currentYear}
            setCurrentYear={setCurrentYear}
            yearRangeStart={yearRangeStart}
            yearRange={yearRange}
          />
        ),
        ...components,
      }}
      numberOfMonths={columnsDisplayed}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

function NavigationComponent({
  className,
  navMode,
  currentYear,
  setCurrentYear,
  yearRangeStart,
  setYearRangeStart,
  yearRange,
}: {
  className?: string
  navMode: NavigationMode
  currentYear: number
  setCurrentYear: (year: number) => void
  yearRangeStart: number
  setYearRangeStart: (start: number) => void
  yearRange: number
}) {
  const { nextMonth, previousMonth, goToMonth } = useDayPicker()

  const handlePrevious = React.useCallback(() => {
    if (navMode === "days" && previousMonth) {
      goToMonth(previousMonth)
    } else if (navMode === "months") {
      setCurrentYear(currentYear - 1)
    } else if (navMode === "years") {
      setYearRangeStart(yearRangeStart - yearRange)
    }
  }, [navMode, previousMonth, goToMonth, currentYear, setCurrentYear, yearRangeStart, setYearRangeStart, yearRange])

  const handleNext = React.useCallback(() => {
    if (navMode === "days" && nextMonth) {
      goToMonth(nextMonth)
    } else if (navMode === "months") {
      setCurrentYear(currentYear + 1)
    } else if (navMode === "years") {
      setYearRangeStart(yearRangeStart + yearRange)
    }
  }, [navMode, nextMonth, goToMonth, currentYear, setCurrentYear, yearRangeStart, setYearRangeStart, yearRange])

  const isPreviousDisabled = navMode === "days" ? !previousMonth : false
  const isNextDisabled = navMode === "days" ? !nextMonth : false

  const getAriaLabel = (direction: "previous" | "next") => {
    if (navMode === "days") {
      return direction === "previous" 
        ? labelPrevious(previousMonth) 
        : labelNext(nextMonth)
    } else if (navMode === "months") {
      return `Go to ${direction === "previous" ? currentYear - 1 : currentYear + 1}`
    } else {
      const range = yearRangeStart + (direction === "previous" ? -yearRange : yearRange)
      return `Go to ${range} - ${range + yearRange - 1}`
    }
  }

  return (
    <nav className={cn("flex items-start", className)}>
      <Button
        variant="outline"
        className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-1"
        type="button"
        disabled={isPreviousDisabled}
        aria-label={getAriaLabel("previous")}
        onClick={handlePrevious}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <Button
        variant="outline"
        className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-1"
        type="button"
        disabled={isNextDisabled}
        aria-label={getAriaLabel("next")}
        onClick={handleNext}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </nav>
  )
}

function CaptionLabelComponent({
  children,
  navMode,
  setNavMode,
  currentYear,
  yearRangeStart,
  yearRange,
  showNavigationSwitcher,
  ...props
}: {
  children?: React.ReactNode
  navMode: NavigationMode
  setNavMode: (mode: NavigationMode) => void
  currentYear: number
  yearRangeStart: number
  yearRange: number
  showNavigationSwitcher: boolean
} & React.HTMLAttributes<HTMLSpanElement>) {
  if (!showNavigationSwitcher) {
    return <span {...props}>{children}</span>
  }

  const handleClick = () => {
    if (navMode === "days") {
      setNavMode("months")
    } else if (navMode === "months") {
      setNavMode("years")
    } else {
      setNavMode("days")
    }
  }

  const getDisplayText = () => {
    if (navMode === "days") {
      return children
    } else if (navMode === "months") {
      return currentYear.toString()
    } else {
      return `${yearRangeStart} - ${yearRangeStart + yearRange - 1}`
    }
  }

  return (
    <Button
      className="h-7 w-full truncate text-sm font-medium"
      variant="ghost"
      size="sm"
      onClick={handleClick}
      {...props}
    >
      {getDisplayText()}
    </Button>
  )
}

function MonthGridComponent({
  className,
  children,
  navMode,
  setNavMode,
  currentYear,
  setCurrentYear,
  yearRangeStart,
  yearRange,
  ...props
}: {
  className?: string
  children?: React.ReactNode
  navMode: NavigationMode
  setNavMode: (mode: NavigationMode) => void
  currentYear: number
  setCurrentYear: (year: number) => void
  yearRangeStart: number
  yearRange: number
} & React.TableHTMLAttributes<HTMLTableElement>) {
  const { goToMonth, selected } = useDayPicker()

  if (navMode === "months") {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ]

    const handleMonthClick = (monthIndex: number) => {
      setNavMode("days")
      goToMonth(new Date(currentYear, monthIndex, 1))
    }

    return (
      <div className="grid grid-cols-3 gap-2 mt-4 w-full" {...props}>
        {months.map((month, index) => {
          const isCurrentMonth = 
            currentYear === new Date().getFullYear() && 
            index === new Date().getMonth()
          
          return (
            <Button
              key={index}
              className={cn(
                "h-9 w-full text-sm font-normal",
                isCurrentMonth && "bg-accent font-medium text-accent-foreground"
              )}
              variant="ghost"
              onClick={() => handleMonthClick(index)}
            >
              {month.slice(0, 3)}
            </Button>
          )
        })}
      </div>
    )
  }

  if (navMode === "years") {
    const years = Array.from({ length: yearRange }, (_, i) => yearRangeStart + i)

    const handleYearClick = (year: number) => {
      setCurrentYear(year)
      setNavMode("months")
    }

    return (
      <div className="grid grid-cols-4 gap-2 mt-4 w-full" {...props}>
        {years.map((year) => {
          const isCurrentYear = year === new Date().getFullYear()
          
          return (
            <Button
              key={year}
              className={cn(
                "h-9 w-full text-sm font-normal",
                isCurrentYear && "bg-accent font-medium text-accent-foreground"
              )}
              variant="ghost"
              onClick={() => handleYearClick(year)}
            >
              {year}
            </Button>
          )
        })}
      </div>
    )
  }

  return (
    <table className={className} {...props}>
      {children}
    </table>
  )
}

export { Calendar }
